// client/src/components/layout/CookieConsent.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CookieConsent.css';

const COOKIE_NAME = 'makah_cookie_consent';
const STORAGE_KEY = 'makah_cookie_preferences';

const defaultPreferences = {
  necessary: true,
  functional: true,
  analytics: true,
};

const setBrowserCookie = (name, value, days = 180) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
};

export const getCookiePreferences = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (err) {
    console.error('Error reading cookie preferences:', err);
  }
  return null;
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);

  useEffect(() => {
    const existingPrefs = getCookiePreferences();
    if (!existingPrefs) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 900);
      return () => clearTimeout(timer);
    } else {
      setPreferences({
        necessary: true,
        functional: Boolean(existingPrefs.functional),
        analytics: Boolean(existingPrefs.analytics),
      });
    }
  }, []);

  useEffect(() => {
    const handleOpenSettings = () => {
      const existingPrefs = getCookiePreferences();
      if (existingPrefs) {
        setPreferences({
          necessary: true,
          functional: Boolean(existingPrefs.functional),
          analytics: Boolean(existingPrefs.analytics),
        });
      }
      setIsClosing(false);
      setIsVisible(true);
      setShowCustomize(true);
    };

    window.addEventListener('makah:open-cookie-settings', handleOpenSettings);
    return () => window.removeEventListener('makah:open-cookie-settings', handleOpenSettings);
  }, []);

  const saveConsent = (newPrefs, statusLabel) => {
    const payload = {
      ...newPrefs,
      necessary: true,
      status: statusLabel,
      updatedAt: new Date().toISOString(),
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      setBrowserCookie(COOKIE_NAME, statusLabel, 180);
      window.dispatchEvent(new CustomEvent('makah:cookie-consent-updated', { detail: payload }));
    } catch (err) {
      console.error('Could not save cookie consent:', err);
    }

    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setShowCustomize(false);
      setIsClosing(false);
    }, 350);
  };

  const handleAcceptAll = () => {
    const allEnabled = { necessary: true, functional: true, analytics: true };
    setPreferences(allEnabled);
    saveConsent(allEnabled, 'accepted_all');
  };

  const handleEssentialOnly = () => {
    const essentialOnly = { necessary: true, functional: false, analytics: false };
    setPreferences(essentialOnly);
    saveConsent(essentialOnly, 'essential_only');
  };

  const handleSaveCustom = () => {
    saveConsent(preferences, 'custom');
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return;
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!isVisible) return null;

  return (
    <div
      className={`cookie-consent-wrapper ${isClosing ? 'cookie-closing' : ''} ${
        showCustomize ? 'has-modal-backdrop' : ''
      }`}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent preferences"
    >
      <div className="cookie-consent-card">
        <div className="cookie-card-top">
          <div className="cookie-icon-badge" aria-hidden="true">
            <i className="fas fa-shield-alt"></i>
          </div>
          <div className="cookie-text-content">
            <div className="cookie-title-row">
              <h3>We Value Your Privacy & Clinical Trust</h3>
              <span className="cookie-badge">Data Protection</span>
            </div>
            <p>
              Makah Medics uses cookies and local storage to ensure secure browsing, remember your
              quote inquiry details, and power interactive features like clinic map directions. Read
              our <Link to="/privacy-policy">Privacy & Cookie Policy</Link> to learn more.
            </p>
          </div>
        </div>

        {showCustomize && (
          <div className="cookie-preferences-panel">
            <div className="cookie-pref-item">
              <div className="cookie-pref-info">
                <div className="cookie-pref-header">
                  <strong>Strictly Necessary Cookies</strong>
                  <span className="pref-status-pill always-on">Always Active</span>
                </div>
                <p>
                  Required for core website navigation, security tokens, and submitting product quote
                  or contact requests.
                </p>
              </div>
              <label className="cookie-switch disabled" aria-label="Strictly Necessary Cookies">
                <input type="checkbox" checked readOnly disabled />
                <span className="cookie-slider"></span>
              </label>
            </div>

            <div className="cookie-pref-item">
              <div className="cookie-pref-info">
                <div className="cookie-pref-header">
                  <strong>Functional & Quote Memory</strong>
                </div>
                <p>
                  Remembers your name, hospital/company, and contact details so you do not need to
                  re-enter them when requesting multiple medical equipment quotes.
                </p>
              </div>
              <label className="cookie-switch" aria-label="Functional Cookies">
                <input
                  type="checkbox"
                  checked={preferences.functional}
                  onChange={() => togglePreference('functional')}
                />
                <span className="cookie-slider"></span>
              </label>
            </div>

            <div className="cookie-pref-item">
              <div className="cookie-pref-info">
                <div className="cookie-pref-header">
                  <strong>Analytics & Third-Party Media</strong>
                </div>
                <p>
                  Enables embedded third-party services (such as Google Maps on our Contact page) and
                  anonymous performance metrics to improve catalog speed.
                </p>
              </div>
              <label className="cookie-switch" aria-label="Analytics and Third-Party Cookies">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={() => togglePreference('analytics')}
                />
                <span className="cookie-slider"></span>
              </label>
            </div>
          </div>
        )}

        <div className="cookie-actions">
          <button
            type="button"
            className="btn-cookie btn-cookie-customize"
            onClick={() => setShowCustomize((prev) => !prev)}
          >
            <i className={`fas ${showCustomize ? 'fa-chevron-up' : 'fa-sliders-h'}`}></i>
            <span>{showCustomize ? 'Hide Preferences' : 'Customize'}</span>
          </button>

          <div className="cookie-primary-actions">
            <button
              type="button"
              className="btn-cookie btn-cookie-essential"
              onClick={handleEssentialOnly}
            >
              Essential Only
            </button>

            {showCustomize ? (
              <button
                type="button"
                className="btn-cookie btn-cookie-accept"
                onClick={handleSaveCustom}
              >
                <i className="fas fa-check"></i>
                <span>Save Preferences</span>
              </button>
            ) : (
              <button
                type="button"
                className="btn-cookie btn-cookie-accept"
                onClick={handleAcceptAll}
              >
                <i className="fas fa-check-circle"></i>
                <span>Accept All Cookies</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
